import { createSlice } from '@reduxjs/toolkit'
import { IBusinessStatsResponse } from '../../features/seller/dashboard/Dashboard.tsx'
import { IAllTransactionsResponse } from '../../interfaces/business/seller/IAllTransactionsResponse.ts'
import { IBusinessOrdersResponse } from '../../interfaces/business/seller/IBusinessOrdersResponse.ts'
import { IStatsResponse } from '../../interfaces/business/seller/IStatsResponse.ts'
import { IBusinessNotificationResponse } from '../../interfaces/seller/IBusinessNotificationResponse.ts'
import { IBusinessOrdersDataResponse } from '../../interfaces/seller/IBusinessOrdersDataResponse.ts'
import { IBusinessProductCountResponse } from '../../interfaces/seller/IBusinessProductCountResponse.ts'
import { IBusinessTransactionsResponse } from '../../interfaces/seller/IBusinessTransactionsResponse.ts'
import { ITopBuyersResponse } from '../../interfaces/seller/ITopBuyersResponse.ts'
import { ITopProductsResponse } from '../../interfaces/seller/ITopProductsResponse.ts'
import {
	getAllBusinessOrdersAction,
	getAllStatsAction,
	getAllTransactionsAction,
	getBusinessNotificationsAction,
	getBusinessProductCountAction,
	getBusinessStatsAction,
	getBusinessTransactionsAction,
	getOrdersByBusinessIdAction,
	getTopBuyersDataAction,
	getTopRatedProductsDataAction,
} from './sellerActions.ts'

interface initialState {
	stats: IStatsResponse
	allBusinessOrders: IBusinessOrdersResponse
	allTransactions: IAllTransactionsResponse
	topBuyers: ITopBuyersResponse
	topRatedProducts: ITopProductsResponse
	BusinessTransactions: IBusinessTransactionsResponse
	ordersByBusiness: IBusinessOrdersDataResponse
	businessNotifications: IBusinessNotificationResponse
	businessProductCount: IBusinessProductCountResponse
	businessStats: IBusinessStatsResponse
	loader: {
		gettingStats: boolean
		gettingAllBusinessOrders: boolean
		gettingAllTransactions: boolean
		gettingTopBuyers: boolean
		gettingTopRatedProducts: boolean
		gettingBusinessTransactions: boolean
		gettingOrdersByBusiness: boolean
		gettingBusinessNotifications: boolean
		gettingBusinessProductCount: boolean
		gettingBusinessStats: boolean
	}
}

const initialState: initialState = {
	allTransactions: {
		success: false,
		transactions: [],
	},
	stats: {
		success: false,
		saleStats: {
			mySale: [],
			myOrders: [],
			storeTraffic: [],
		},
	},
	allBusinessOrders: {
		success: false,
		length: 0,
		orders: [],
	},
	topBuyers: {
		success: false,
		data: [],
	},
	topRatedProducts: {
		success: false,
		data: [],
	},
	BusinessTransactions: {
		success: false,
		transactions: [],
	},
	ordersByBusiness: {
		success: false,
		orders: [],
		totalPages: 0,
		hasNext: false,
	},
	businessNotifications: {
		success: false,
		data: [],
	},
	businessProductCount: {
		success: false,
		data: {
			totalProducts: 0,
			inStock: 0,
			outOfStock: 0,
			fewLeft: 0,
		},
	},
	businessStats: {
		success: false,
		data: {
			totalRevenue: 0,
		},
	},
	loader: {
		gettingStats: false,
		gettingAllBusinessOrders: false,
		gettingAllTransactions: false,
		gettingTopBuyers: false,
		gettingTopRatedProducts: false,
		gettingBusinessTransactions: false,
		gettingOrdersByBusiness: false,
		gettingBusinessNotifications: false,
		gettingBusinessProductCount: false,
		gettingBusinessStats: false,
	},
}

const sellerSlice = createSlice({
	name: 'seller',
	initialState,
	reducers: {},
	extraReducers: (builder) =>
		builder
			// get all stats actions
			.addCase(getAllStatsAction.pending, (state) => {
				state.loader.gettingStats = true
			})
			.addCase(getAllStatsAction.fulfilled, (state, action) => {
				state.loader.gettingStats = false
				state.stats = action.payload
			})
			.addCase(getAllStatsAction.rejected, (state) => {
				state.loader.gettingStats = false
			})
			// get all transactions
			.addCase(getAllTransactionsAction.pending, (state) => {
				state.loader.gettingAllTransactions = true
			})
			.addCase(getAllTransactionsAction.fulfilled, (state, action) => {
				state.loader.gettingAllTransactions = false
				if (!action.payload) return
				state.allTransactions = action.payload
			})
			.addCase(getAllTransactionsAction.rejected, (state) => {
				state.loader.gettingAllTransactions = false
			})
			// get all business orders
			.addCase(getAllBusinessOrdersAction.pending, (state) => {
				state.loader.gettingAllBusinessOrders = true
			})
			.addCase(getAllBusinessOrdersAction.fulfilled, (state, action) => {
				state.loader.gettingAllBusinessOrders = false
				if (!action.payload) return
				state.allBusinessOrders = action.payload
			})
			.addCase(getAllBusinessOrdersAction.rejected, (state) => {
				state.loader.gettingAllBusinessOrders = false
			})
			// getting top buyers
			.addCase(getTopBuyersDataAction.pending, (state) => {
				state.loader.gettingTopBuyers = true
			})
			.addCase(getTopBuyersDataAction.fulfilled, (state, action) => {
				state.loader.gettingTopBuyers = false
				if (!action.payload) return
				state.topBuyers = action.payload
			})
			.addCase(getTopBuyersDataAction.rejected, (state) => {
				state.loader.gettingTopBuyers = false
			})
			// getting top rated products
			.addCase(getTopRatedProductsDataAction.pending, (state) => {
				state.loader.gettingTopRatedProducts = true
			})
			.addCase(getTopRatedProductsDataAction.fulfilled, (state, action) => {
				state.loader.gettingTopRatedProducts = false
				if (!action.payload) return
				state.topRatedProducts = action.payload
			})
			.addCase(getTopRatedProductsDataAction.rejected, (state) => {
				state.loader.gettingTopRatedProducts = false
			})
			// getting business transactions
			.addCase(getBusinessTransactionsAction.pending, (state) => {
				state.loader.gettingBusinessTransactions = true
			})
			.addCase(getBusinessTransactionsAction.fulfilled, (state, action) => {
				state.loader.gettingBusinessTransactions = false
				if (!action.payload) return
				state.BusinessTransactions = action.payload
			})
			.addCase(getBusinessTransactionsAction.rejected, (state) => {
				state.loader.gettingBusinessTransactions = false
			})
			// getting orders by business
			.addCase(getOrdersByBusinessIdAction.pending, (state) => {
				state.loader.gettingOrdersByBusiness = true
			})
			.addCase(getOrdersByBusinessIdAction.fulfilled, (state, action) => {
				state.loader.gettingOrdersByBusiness = false
				if (!action.payload) return
				state.ordersByBusiness = action.payload
			})
			.addCase(getOrdersByBusinessIdAction.rejected, (state) => {
				state.loader.gettingOrdersByBusiness = false
			})
			// getting notifications by business
			.addCase(getBusinessNotificationsAction.pending, (state) => {
				state.loader.gettingBusinessNotifications = true
			})
			.addCase(getBusinessNotificationsAction.fulfilled, (state, action) => {
				state.loader.gettingBusinessNotifications = false
				if (!action.payload) return
				state.businessNotifications = action.payload
			})
			.addCase(getBusinessNotificationsAction.rejected, (state) => {
				state.loader.gettingBusinessNotifications = false
			})
			// getting business product count
			.addCase(getBusinessProductCountAction.pending, (state) => {
				state.loader.gettingBusinessProductCount = true
			})
			.addCase(getBusinessProductCountAction.fulfilled, (state, action) => {
				state.loader.gettingBusinessProductCount = false
				if (!action.payload) return
				state.businessProductCount = action.payload
			})
			.addCase(getBusinessProductCountAction.rejected, (state) => {
				state.loader.gettingBusinessProductCount = false
			})
			// getting business stats
			.addCase(getBusinessStatsAction.pending, (state) => {
				state.loader.gettingBusinessStats = true
			})
			.addCase(getBusinessStatsAction.fulfilled, (state, action) => {
				state.loader.gettingBusinessStats = false
				if (!action.payload) return
				state.businessStats = action.payload
			})
			.addCase(getBusinessStatsAction.rejected, (state) => {
				state.loader.gettingBusinessStats = false
			}),
})

const sellerReducer = sellerSlice.reducer
export default sellerReducer
